import { Controller } from "@hotwired/stimulus"

/**
 * Validation Controller - Real-time inline validation
 * Provides instant feedback on field validation errors
 */
export default class extends Controller {
  static targets = ["field", "error"]
  static values = {
    url: String,
    debounce: { type: Number, default: 500 }
  }

  connect() {
    console.log('[validation] Controller connected')

    // Create debounced validate function
    this.debouncedValidate = this.debounce(
      this.performValidation.bind(this),
      this.debounceValue
    )
  }

  disconnect() {
    console.log('[validation] Controller disconnected')

    // Cancel pending validations
    if (this.debouncedValidate && this.debouncedValidate.cancel) {
      this.debouncedValidate.cancel()
    }
  }

  /**
   * Validate field (debounced)
   * @param {Event} event - Input/blur event
   */
  validate(event) {
    const field = event.currentTarget
    const fieldName = field.name
    const fieldValue = field.value

    console.log('[validation] Validation triggered for:', fieldName)

    // Show loading state
    this.showValidating(field)

    // Debounced validation
    this.debouncedValidate(field, fieldName, fieldValue)
  }

  /**
   * Perform the actual validation
   * @param {Element} field - Field element
   * @param {string} fieldName - Field name
   * @param {string} fieldValue - Field value
   */
  async performValidation(field, fieldName, fieldValue) {
    if (!this.hasUrlValue) {
      console.warn('[validation] No URL configured for validation')
      return
    }

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          field: fieldName,
          value: fieldValue
        })
      })

      const data = await response.json()

      if (data.valid) {
        this.showValid(field)
        this.clearErrors(field)
      } else {
        this.showInvalid(field)
        this.showErrors(field, data.errors)
      }
    } catch (error) {
      console.error('[validation] Validation request failed:', error)
      // Fallback to browser validation
      this.useBrowserValidation(field)
    }
  }

  /**
   * Use browser's built-in validation
   * @param {Element} field - Field element
   */
  useBrowserValidation(field) {
    if (field.validity.valid) {
      this.showValid(field)
      this.clearErrors(field)
    } else {
      this.showInvalid(field)
      this.showErrors(field, [field.validationMessage])
    }
  }

  /**
   * Show validating state
   * @param {Element} field - Field element
   */
  showValidating(field) {
    field.classList.remove('is-valid', 'is-invalid')
    field.classList.add('is-validating')
  }

  /**
   * Show valid state
   * @param {Element} field - Field element
   */
  showValid(field) {
    field.classList.remove('is-invalid', 'is-validating')
    field.classList.add('is-valid')
  }

  /**
   * Show invalid state
   * @param {Element} field - Field element
   */
  showInvalid(field) {
    field.classList.remove('is-valid', 'is-validating')
    field.classList.add('is-invalid')
  }

  /**
   * Show error messages
   * @param {Element} field - Field element
   * @param {Array} errors - Error messages
   */
  showErrors(field, errors) {
    const fieldName = field.name
    const errorContainer = this.element.querySelector(`#${fieldName}_errors`)

    if (errorContainer) {
      errorContainer.innerHTML = errors.map(error =>
        `<div class="invalid-feedback d-block">${error}</div>`
      ).join('')
    }
  }

  /**
   * Clear error messages
   * @param {Element} field - Field element
   */
  clearErrors(field) {
    const fieldName = field.name
    const errorContainer = this.element.querySelector(`#${fieldName}_errors`)

    if (errorContainer) {
      errorContainer.innerHTML = ''
    }
  }

  /**
   * Debounce function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout

    const debounced = (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }

    debounced.cancel = () => {
      clearTimeout(timeout)
    }

    return debounced
  }

  /**
   * Get CSRF token
   * @returns {string} CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
